// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\MerchantAllInOne.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MerchantLayout from "../components/MerchantLayout";
import { apiGet, apiPost } from "../api";

/**
 * ✅ Merchant combined page (ONE screen):
 * 1) 全データ提出（Global Submit）
 * 2) フォーム入力（7フォーム）
 * 3) 書類アップロード
 *
 * ✅ Color rule (Merchant side only) — PER ITEM:
 * - 提出前:
 *    - 未着手 → 赤
 *    - 開始済（フォーム入力済 / 書類アップロード済）→ 青
 * - 提出後（承認待ち/承認済含む）→ 全て 灰（ロック）
 *
 * ✅ Even after approval, Merchant side stays 灰 (no green).
 */

type FormKey =
  | "company-overview"
  | "representative"
  | "payment-handling"
  | "products"
  | "sales-method"
  | "store-list"
  | "contact-person";

type FormCard = {
  key: FormKey;
  label: string;
  path: string;
  statusEndpoint: string;
};

type MerchantMe = {
  merchant_id: string;
  company_name: string;

  status: string; // 未着手 / 進行中 / 提出済（承認待ち） / 承認済

  is_submitted?: number;
  submitted_at?: string | null;

  approval_status?: string; // 未承認 / 承認済
  approved_at?: string | null;

  merchant_type?: string; // 個人 / 法人
  business_type?: string; // 美容医療 etc.
};

type UploadedDocItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

type UploadKey =
  | "kessansho_main"
  | "kakutei_shinkoku"
  | "touhon"
  | "doctor_license"
  | "kaisetsu_todoke"
  | "kessansho_related"
  | "business_plan"
  | "other_1"
  | "other_2"
  | "other_3"
  | "other_4"
  | "other_5";

type UploadItem = {
  key: UploadKey;
  label: string;
  required: boolean;
  allowed: string;
  note?: string;
  isOther?: boolean;
};

type UiState = "red" | "blue" | "grey";

const FORM_LIST: FormCard[] = [
  {
    key: "company-overview",
    label: "1. 会社概要（全業種共通）",
    path: "/merchant/forms/company-overview",
    statusEndpoint: "/merchant/forms/company-overview",
  },
  {
    key: "representative",
    label: "2. 代表者様情報",
    path: "/merchant/forms/representative",
    statusEndpoint: "/merchant/forms/representative",
  },
  {
    key: "payment-handling",
    label: "3. 決済取扱状況",
    path: "/merchant/forms/payment-handling",
    statusEndpoint: "/merchant/forms/payment-handling",
  },
  {
    key: "products",
    label: "4. 取扱商品",
    path: "/merchant/forms/products",
    statusEndpoint: "/merchant/forms/products",
  },
  {
    key: "sales-method",
    label: "5. 販売方法",
    path: "/merchant/forms/sales-method",
    statusEndpoint: "/merchant/forms/sales-method",
  },
  {
    key: "store-list",
    label: "6. 店舗一覧",
    path: "/merchant/forms/store-list",
    statusEndpoint: "/merchant/forms/store-list",
  },
  {
    key: "contact-person",
    label: "7. ご担当者様情報",
    path: "/merchant/forms/contact-person",
    statusEndpoint: "/merchant/forms/contact-person",
  },
];

const ALL_UPLOAD_KEYS: UploadKey[] = [
  "kessansho_main",
  "kakutei_shinkoku",
  "touhon",
  "doctor_license",
  "kaisetsu_todoke",
  "kessansho_related",
  "business_plan",
  "other_1",
  "other_2",
  "other_3",
  "other_4",
  "other_5",
];

function makeBoolMapFalse(): Record<UploadKey, boolean> {
  return ALL_UPLOAD_KEYS.reduce((acc, k) => {
    acc[k] = false;
    return acc;
  }, {} as Record<UploadKey, boolean>);
}

function makeStringMapEmpty(): Record<UploadKey, string> {
  return ALL_UPLOAD_KEYS.reduce((acc, k) => {
    acc[k] = "";
    return acc;
  }, {} as Record<UploadKey, string>);
}

function isUploadKey(v: string): v is UploadKey {
  return (ALL_UPLOAD_KEYS as string[]).includes(v);
}

function isSubmittedOrApproved(me: MerchantMe | null): boolean {
  if (!me) return false;
  const s = (me.status || "").trim();
  const ap = (me.approval_status || "").trim();
  const sub = Number(me.is_submitted || 0) === 1;
  return sub || s === "提出済（承認待ち）" || s === "承認済" || ap === "承認済";
}

function itemUiState(opts: { submittedOrApproved: boolean; started: boolean }): UiState {
  if (opts.submittedOrApproved) return "grey";
  return opts.started ? "blue" : "red";
}

function colorsFor(ui: UiState) {
  if (ui === "grey") {
    return {
      cardBg: "#f1f5f9",
      cardBorder: "1px solid #cbd5e1",
      chipBg: "#64748b",
      chipText: "#fff",
    };
  }
  if (ui === "blue") {
    return {
      cardBg: "#eff6ff",
      cardBorder: "1px solid #93c5fd",
      chipBg: "#1d4ed8",
      chipText: "#fff",
    };
  }
  return {
    cardBg: "#fff1f2",
    cardBorder: "1px solid #fecdd3",
    chipBg: "#dc2626",
    chipText: "#fff",
  };
}

export default function MerchantAllInOne() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uploadsSectionRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [uploadDebugMsg, setUploadDebugMsg] = useState("");

  const [me, setMe] = useState<MerchantMe | null>(null);

  const [formCompleted, setFormCompleted] = useState<Record<FormKey, boolean>>({
    "company-overview": false,
    representative: false,
    "payment-handling": false,
    products: false,
    "sales-method": false,
    "store-list": false,
    "contact-person": false,
  });

  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocItem[]>([]);
  const [requiredDocLabels, setRequiredDocLabels] = useState<string[] | null>(null);
  const [missingRequiredLabels, setMissingRequiredLabels] = useState<string[] | null>(null);

  const [uploaded, setUploaded] = useState<Record<UploadKey, boolean>>(makeBoolMapFalse());
  const [fileNames, setFileNames] = useState<Record<UploadKey, string>>(makeStringMapEmpty());

  const [otherTitles, setOtherTitles] = useState<Record<string, string>>({
    other_1: "",
    other_2: "",
    other_3: "",
    other_4: "",
    other_5: "",
  });

  const [uploading, setUploading] = useState<Record<UploadKey, boolean>>(
    ALL_UPLOAD_KEYS.reduce((acc, k) => {
      acc[k] = false;
      return acc;
    }, {} as Record<UploadKey, boolean>)
  );

  const [submitMessage, setSubmitMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ✅ Important: refs for file inputs (fix label click issues)
  const fileInputRefs = useRef<Partial<Record<UploadKey, HTMLInputElement | null>>>({});

  // ✅ FIX: when file dialog closes, window "focus" fires -> loadAll() re-renders -> input onChange can be lost.
  // We skip ONE focus refresh right after opening the file dialog.
  const skipNextFocusReload = useRef(false);
  const hasRedirectedForMissingType = useRef(false);

  const submittedOrApproved = useMemo(() => isSubmittedOrApproved(me), [me]);

  const formsCompletedCount = useMemo(
    () => Object.values(formCompleted).filter(Boolean).length,
    [formCompleted]
  );
  const formsAllDone = useMemo(() => formsCompletedCount === 7, [formsCompletedCount]);

  // merchant type gating for uploads
  const merchantType = (me?.merchant_type || "").trim();
  const businessType = (me?.business_type || "").trim();
  const typeSelected = merchantType === "個人" || merchantType === "法人";

  const uploadItems: UploadItem[] = useMemo(() => {
    if (!typeSelected) return [];

    const isBeautyMedical = (businessType || "").trim() === "美容医療";

    const baseRequired: UploadItem[] =
      merchantType === "個人"
        ? [{ key: "kakutei_shinkoku", label: "確定申告（直近3年分）", required: true, allowed: "PDF / JPG" }]
        : [
            { key: "kessansho_main", label: "決算書（直近3期分）", required: true, allowed: "PDF / JPG" },
            { key: "touhon", label: "謄本（履歴事項全部証明書）", required: true, allowed: "PDF / JPG" },
          ];

    const beautyRequired: UploadItem[] = isBeautyMedical
      ? [
          {
            key: "doctor_license",
            label: merchantType === "個人" ? "院長医師免許証" : "理事長医師免許証",
            required: true,
            allowed: "PDF / JPG",
            note: "※ 美容医療のみ",
          },
          { key: "kaisetsu_todoke", label: "開設届", required: true, allowed: "PDF / JPG", note: "※ 美容医療のみ" },
        ]
      : [];

    const optional: UploadItem[] = [
      {
        key: "kessansho_related",
        label: "決算書（運営会社・MS法人・関連会社）",
        required: false,
        allowed: "PDF / JPG",
      },
      { key: "business_plan", label: "事業計画書", required: false, allowed: "PDF / JPG / Excel / Word" },
      { key: "other_1", label: "その他①", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
      { key: "other_2", label: "その他②", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
      { key: "other_3", label: "その他③", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
      { key: "other_4", label: "その他④", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
      { key: "other_5", label: "その他⑤", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
    ];

    return [...baseRequired, ...beautyRequired, ...optional];
  }, [typeSelected, merchantType, businessType]);

  // ✅ If backend does NOT send missing_required_labels, compute it here
  const computedMissingLabels = useMemo(() => {
    if (!typeSelected) return null;

    // If backend provides it, use backend
    if (missingRequiredLabels !== null) return missingRequiredLabels;

    const isBeautyMedical = (businessType || "").trim() === "美容医療";

    const missing: string[] = [];
    for (const it of uploadItems) {
      if (!it.required) continue;
      if (!uploaded[it.key]) missing.push(it.label);
    }

    // (The requiredLabels array is not needed anymore; uploadItems already represents requirements.)
    // Keep logic minimal and correct.

    // If beauty medical but uploadItems not ready for some reason, this will be covered on next render.
    if (isBeautyMedical) {
      // no-op
    }

    return missing;
  }, [typeSelected, missingRequiredLabels, businessType, uploadItems, uploaded]);

  const requiredDocsDone = useMemo(() => {
    if (computedMissingLabels === null) return false;
    return computedMissingLabels.length === 0;
  }, [computedMissingLabels]);

  const canSubmit = useMemo(() => {
    if (!me) return false;
    if (submittedOrApproved) return false;
    return formsAllDone && requiredDocsDone;
  }, [me, submittedOrApproved, formsAllDone, requiredDocsDone]);

  const anyFormStarted = useMemo(() => formsCompletedCount > 0, [formsCompletedCount]);
  const anyUploadStarted = useMemo(() => uploadedDocs.length > 0, [uploadedDocs.length]);
  const bannerState: UiState = useMemo(() => {
    if (submittedOrApproved) return "grey";
    if (anyFormStarted || anyUploadStarted) return "blue";
    return "red";
  }, [submittedOrApproved, anyFormStarted, anyUploadStarted]);

  const BANNER_COLORS = useMemo(() => colorsFor(bannerState), [bannerState]);

  async function loadAll(signal?: AbortSignal) {
    setLoading(true);
    setPageError("");
    setSubmitMessage("");
    setUploadDebugMsg("");

    try {
      const meRes = await apiGet("/merchant/me");
      const merchant: MerchantMe = meRes?.merchant;
      if (!merchant) throw new Error("加盟店情報の取得に失敗しました。");
      if (signal?.aborted) return;
      setMe(merchant);


    


      const hasMerchantType =
        (merchant.merchant_type || "").trim() === "個人" ||
        (merchant.merchant_type || "").trim() === "法人";

      if (!hasMerchantType) {
        if (!hasRedirectedForMissingType.current) {
          hasRedirectedForMissingType.current = true;
          window.alert("区分（個人／法人）を先に選択してください。");
          navigate("/merchant/dashboard");
        }
        return;
      }



      const nextFormCompleted: Record<FormKey, boolean> = {
        "company-overview": false,
        representative: false,
        "payment-handling": false,
        products: false,
        "sales-method": false,
        "store-list": false,
        "contact-person": false,
      };

      const formCalls = await Promise.allSettled(
        FORM_LIST.map(async (f) => {
          const r = await apiGet(f.statusEndpoint);
          const done = Boolean(r?.data?.is_completed ?? r?.is_completed);
          return { key: f.key, done };
        })
      );

      for (const r of formCalls) {
        if (r.status === "fulfilled") {
          nextFormCompleted[r.value.key] = r.value.done;
        }
      }
      if (signal?.aborted) return;
      setFormCompleted(nextFormCompleted);

      const statusRes = await apiGet("/merchant/documents/status");
      const rows: UploadedDocItem[] = Array.isArray(statusRes?.items) ? statusRes.items : [];
      if (signal?.aborted) return;
      setUploadedDocs(rows);

      const requiredLabels: string[] | null = Array.isArray(statusRes?.required_labels)
        ? statusRes.required_labels
        : Array.isArray(statusRes?.required)
        ? statusRes.required.map((x: any) => String(x?.label || "")).filter((s: string) => s.trim())
        : null;

      const missingLabels: string[] | null = Array.isArray(statusRes?.missing_required_labels)
        ? statusRes.missing_required_labels
        : Array.isArray(statusRes?.missing_required)
        ? statusRes.missing_required.map((x: any) => String(x?.label || "")).filter((s: string) => s.trim())
        : null;

      setRequiredDocLabels(requiredLabels);
      setMissingRequiredLabels(missingLabels);

      const nextUploaded = makeBoolMapFalse();
      const nextFileNames = makeStringMapEmpty();
      const nextOtherTitles: Record<string, string> = {
        other_1: "",
        other_2: "",
        other_3: "",
        other_4: "",
        other_5: "",
      };

      for (const r of rows) {
        const rawKey = r?.document_key || "";
        const rawLabel = r?.document_label || "";

        const mappedKey =
          (merchant?.merchant_type || "").trim() === "個人" &&
          rawKey === "kessansho_main" &&
          String(rawLabel).includes("確定申告")
            ? "kakutei_shinkoku"
            : rawKey;

        if (!isUploadKey(mappedKey)) continue;

        nextUploaded[mappedKey] = true;
        nextFileNames[mappedKey] = r?.file_name || "";
        if (mappedKey.startsWith("other_")) {
          nextOtherTitles[mappedKey] = r?.other_title || "";
        }
      }

      setUploaded(nextUploaded);
      setFileNames(nextFileNames);
      setOtherTitles((prev) => ({ ...prev, ...nextOtherTitles }));

      console.log("[LOAD] MerchantAllInOne state", {
        merchant_type: (merchant?.merchant_type || "").trim(),
        business_type: (merchant?.business_type || "").trim(),
        submittedOrApproved: isSubmittedOrApproved(merchant),
        uploadedDocsCount: rows.length,
        missingRequiredLabelsCount: missingLabels ? missingLabels.length : null,
      });
    } catch (e: any) {
      if (signal?.aborted) return;
      setPageError(e?.message || "データの取得に失敗しました。");
    } finally {
      if (signal?.aborted) return;
      setLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    loadAll(controller.signal);

    const onFocus = () => {
      // ✅ If focus comes back from file picker, skip one reload so input onChange can fire.
      if (skipNextFocusReload.current) {
        skipNextFocusReload.current = false;
        return;
      }
      loadAll();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // ✅ same protection (some browsers trigger visibilitychange with file dialog)
        if (skipNextFocusReload.current) {
          skipNextFocusReload.current = false;
          return;
        }
        loadAll();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      controller.abort();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  if (loading) return;

  if (searchParams.get("section") === "uploads") {
    setTimeout(() => {
      uploadsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }
}, [loading, searchParams]);

  const onSubmitAll = async () => {
    setPageError("");
    setSubmitMessage("");
    setUploadDebugMsg("");

    if (!me) return;

    if (submittedOrApproved) {
      setSubmitMessage("すでに提出済みです。");
      return;
    }

    if (!canSubmit) {
      setSubmitMessage("未完了の項目があります。フォーム入力と必須書類の提出を完了してください。");
      return;
    }

    const ok = window.confirm("すべてのデータを提出します。提出後は修正できません。よろしいですか？");
    if (!ok) return;

    setSubmitting(true);
    try {
      await apiPost("/merchant/submit", {});
      setSubmitMessage("提出が完了しました。ありがとうございました。");
      await loadAll();
    } catch (e: any) {
      setPageError(e?.message || "提出に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  const onPickFile = async (it: UploadItem, file: File | null) => {
    console.log("[UPLOAD] onPickFile called", {
      key: it.key,
      label: it.label,
      file: file ? { name: file.name, size: file.size, type: file.type } : null,
      submittedOrApproved,
      typeSelected,
      uploadedAlready: uploaded[it.key],
    });

    setUploadDebugMsg("");

    if (!file) return;

    if (submittedOrApproved) {
      setUploadDebugMsg("Upload blocked: already submitted/locked.");
      return;
    }

    if (!typeSelected) {
      setUploadDebugMsg("Upload blocked: merchant type is not selected (個人/法人).");
      return;
    }

    //if (uploaded[it.key]) {
      //setUploadDebugMsg("Upload blocked: this document is already uploaded.");
      //return;
    //}

    setPageError("");
    setUploading((p) => ({ ...p, [it.key]: true }));

    try {
      const fd = new FormData();
      fd.append("document_key", it.key);
      fd.append("document_label", it.label);
      fd.append("other_title", it.isOther ? (otherTitles[it.key] || "") : "");
      fd.append("upload_file", file);

      console.log("[UPLOAD] Sending POST /merchant/documents/upload ...");

      const res = await apiPost("/merchant/documents/upload", fd);
      console.log("[UPLOAD] Upload success:", res);

      const savedName = (res?.file_name || file.name) as string;

      setUploaded((prev) => ({ ...prev, [it.key]: true }));
      setFileNames((prev) => ({ ...prev, [it.key]: savedName }));

      if (it.isOther) {
        setOtherTitles((p) => ({ ...p, [it.key]: otherTitles[it.key] || "" }));
      }

      // refresh list + required
      try {
        const statusRes = await apiGet("/merchant/documents/status");
        const rows: UploadedDocItem[] = Array.isArray(statusRes?.items) ? statusRes.items : [];
        setUploadedDocs(rows);

        const missingLabels: string[] | null = Array.isArray(statusRes?.missing_required_labels)
          ? statusRes.missing_required_labels
          : Array.isArray(statusRes?.missing_required)
          ? statusRes.missing_required.map((x: any) => String(x?.label || "")).filter((s: string) => s.trim())
          : null;

        setMissingRequiredLabels(missingLabels);
      } catch {
        // ignore
      }
    } catch (e: any) {
      console.log("[UPLOAD] Upload failed:", e);
      setPageError(e?.message || "アップロードに失敗しました。");
    } finally {
      setUploading((p) => ({ ...p, [it.key]: false }));
    }
  };

  const submitBtnStyle: React.CSSProperties = {
    padding: "12px 18px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: submittedOrApproved
      ? "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"
      : !canSubmit || submitting
      ? "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"
      : "linear-gradient(90deg, #ef4444 0%, #b91c1c 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: submittedOrApproved || !canSubmit || submitting ? "not-allowed" : "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
    minWidth: 140,
  };

  const submitBtnLabel = submittedOrApproved ? "提出済み" : submitting ? "提出中..." : "全データ提出";

  return (
    <MerchantLayout title="加盟店登録" active="forms">
      <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>加盟店登録</div>

      <div style={{ color: "#475569", marginBottom: 12, fontWeight: 800 }}>
        フォーム入力 → 書類アップロード → すべて完了後に提出してください。
      </div>

      <div
        style={{
          marginBottom: 14,
          padding: "10px 12px",
          borderRadius: 12,
          background: BANNER_COLORS.cardBg,
          border: BANNER_COLORS.cardBorder,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div style={{ fontWeight: 900, color: "#0f172a" }}>進捗ステータス</div>
        <div
          style={{
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 900,
            background: BANNER_COLORS.chipBg,
            color: BANNER_COLORS.chipText,
            whiteSpace: "nowrap",
          }}
        >
          {bannerState === "red" ? "未着手" : bannerState === "blue" ? "進行中" : "提出済（編集不可）"}
        </div>
      </div>

      {pageError && (
        <div
          style={{
            marginBottom: 14,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {pageError}
        </div>
      )}

      {submitMessage && (
        <div
          style={{
            marginBottom: 14,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e3a8a",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {submitMessage}
        </div>
      )}

      {uploadDebugMsg && (
        <div
          style={{
            marginBottom: 14,
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            color: "#92400e",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {uploadDebugMsg}
        </div>
      )}

      {loading ? (
        <div style={{ padding: "12px 0", color: "#334155", fontWeight: 900 }}>読み込み中...</div>
      ) : (
        <>
          {/* 1) Global Submit */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 6 }}>全データ提出</div>
                <div style={{ color: "#475569", fontWeight: 900, fontSize: 13, lineHeight: 1.6 }}>
                  フォーム入力と書類アップロード（必須）がすべて完了したら提出してください。
                  <br />
                  <span style={{ color: "#b91c1c" }}>提出後は編集できません。</span>
                </div>
              </div>

              <button
                onClick={onSubmitAll}
                disabled={submittedOrApproved || !canSubmit || submitting}
                style={submitBtnStyle}
              >
                {submitBtnLabel}
              </button>
            </div>

            {!canSubmit && !submittedOrApproved && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#b45309", fontWeight: 900 }}>
                ※ 提出するには「フォーム入力（7フォーム）」と「書類アップロード（必須）」がすべて完了している必要があります。
              </div>
            )}

            {submittedOrApproved && (
              <div
                style={{
                  marginTop: 10,
                  background: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  color: "#334155",
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontWeight: 900,
                  fontSize: 13,
                }}
              >
                提出済みです。編集はできません。
              </div>
            )}
          </div>

          {/* 2) Forms */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>フォーム入力（7フォーム）</div>
              <div style={{ fontWeight: 900, color: "#0b1f3b" }}>{formsCompletedCount}/7</div>
            </div>

            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              {FORM_LIST.map((it) => {
                const done = formCompleted[it.key];
                const itemState = itemUiState({ submittedOrApproved, started: done });
                const C = colorsFor(itemState);
                const chipText = done ? "入力済み" : "未完了";

                return (
                  <button
                    key={it.key}
                    onClick={() => navigate(it.path)}
                    style={{
                      textAlign: "left",
                      padding: "14px 14px",
                      borderRadius: 14,
                      border: C.cardBorder,
                      background: C.cardBg,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a" }}>{it.label}</div>
                      <div
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          background: C.chipBg,
                          color: "#fff",
                        }}
                      >
                        {chipText}
                      </div>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                      {submittedOrApproved ? "提出済み（編集不可）" : done ? "必須項目入力済み" : "未入力 / 未保存"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3) Uploads */}
<div
  ref={uploadsSectionRef}
  style={{
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
    marginBottom: 6,
  }}
>
            <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 10 }}>書類アップロード</div>

            <div style={{ color: "#475569", marginBottom: 12, fontWeight: 800 }}>
              必要書類をアップロードしてください。必須書類がすべて提出済みになると完了になります。
              <br />
              <span style={{ color: "#b91c1c" }}>提出後は書類の差し替えができません。</span>
            </div>

            {!typeSelected && (
              <div
                style={{
                  marginBottom: 14,
                  background: "#fffbeb",
                  border: "1px solid #f59e0b",
                  color: "#92400e",
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontWeight: 900,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                ⚠ 区分（個人／法人）を選択してください。未選択の場合は書類アップロードができません。
              </div>
            )}

            {typeSelected && computedMissingLabels !== null && (
              <div
                style={{
                  marginBottom: 14,
                  background: computedMissingLabels.length === 0 ? "#ecfdf5" : "#fffbeb",
                  border: `1px solid ${
                    computedMissingLabels.length === 0 ? "#86efac" : "rgba(251, 146, 60, 0.6)"
                  }`,
                  color: computedMissingLabels.length === 0 ? "#166534" : "#92400e",
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontWeight: 900,
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {computedMissingLabels.length === 0
                  ? "必須書類：すべて提出済みです。"
                  : `必須書類：未提出が${computedMissingLabels.length}件あります。`}
              </div>
            )}

            {!typeSelected ? null : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                {uploadItems.map((it) => {
                  const done = uploaded[it.key];
                  const isUploading = uploading[it.key];
                  const uploadDisabled = submittedOrApproved || isUploading;

                  const itemState = itemUiState({ submittedOrApproved, started: done });
const C = it.required
  ? colorsFor(itemState)
  : colorsFor(submittedOrApproved ? "grey" : "grey");

                  return (
                    <div
                      key={it.key}
                      style={{
                        textAlign: "left",
                        padding: "14px 14px",
                        borderRadius: 14,
                        border: C.cardBorder,
                        background: C.cardBg,
                        opacity: isUploading ? 0.85 : 1,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>

                        <div style={{ fontWeight: 900, color: "#0f172a" }}>{it.label}</div>

                        <div
  style={{
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    background: submittedOrApproved
      ? "#64748b"
      : done
      ? "#16a34a"
      : "#dc2626",
    color: "#fff",
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
  }}
>
  {done ? "提出済" : "未提出"}
</div>
                      </div>

                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          color: "#64748b",
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
  style={{
    padding: "5px 12px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 13,
    background: it.required ? "#dc2626" : "#e0f2fe",
    color: it.required ? "#ffffff" : "#0369a1",
    border: it.required ? "2px solid #991b1b" : "2px solid #7dd3fc",
    boxShadow: it.required ? "0 2px 6px rgba(220, 38, 38, 0.25)" : "none",
  }}
>
  {it.required ? "必須書類" : "任意書類"}
</span>

                        <span style={{ fontWeight: 800 }}>形式：{it.allowed}</span>
                        {it.note && <span style={{ fontWeight: 800 }}>{it.note}</span>}
                      </div>

                      {it.isOther && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                            書類名（任意）
                          </div>
                          <input
                            value={otherTitles[it.key] || ""}
                            onChange={(e) => setOtherTitles((p) => ({ ...p, [it.key]: e.target.value }))}
                            placeholder="例：賃貸契約書 / 会社案内 / 見積書 など"
                            disabled={submittedOrApproved || done || isUploading}
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                              padding: "10px 12px",
                              borderRadius: 12,
                              border: "1px solid #cbd5e1",
                              outline: "none",
                              fontWeight: 700,
                              background: submittedOrApproved || done ? "#f1f5f9" : "#fff",
                              cursor: submittedOrApproved || done ? "not-allowed" : "text",
                            }}
                          />
                        </div>
                      )}

                      <div style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (uploadDisabled) return;

                            // ✅ critical fix: prevent focus reload from killing input onChange
                            skipNextFocusReload.current = true;

                            fileInputRefs.current[it.key]?.click();
                          }}
                          disabled={uploadDisabled}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 16px",
                            borderRadius: 12,
                            border: "1px solid rgba(0,0,0,0.12)",
                            background: uploadDisabled
                              ? "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"
                              : "linear-gradient(90deg, #2e5f9e 0%, #1d4ed8 100%)",
                            color: "#fff",
                            fontWeight: 900,
                            cursor: uploadDisabled ? "not-allowed" : "pointer",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                            opacity: uploadDisabled ? 0.85 : 1,
                          }}
                          title={
  submittedOrApproved
    ? "提出済みのためアップロードできません"
    : !done
    ? "ファイルを選択してアップロードします"
    : undefined
}
                        >
                          {isUploading ? "アップロード中..." : done ? "提出済み" : "アップロード"}
                        </button>

                        <input
                          ref={(el) => {
                            fileInputRefs.current[it.key] = el;
                          }}
                          type="file"
                          style={{ display: "none" }}
                          disabled={uploadDisabled}
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            e.currentTarget.value = "";
                            onPickFile(it, f);
                          }}
                        />

                        {fileNames[it.key] && (
  <div style={{ marginTop: 8 }}>
    <div
      style={{
        fontSize: 12,
        color: "#334155",
        fontWeight: 800,
        marginBottom: 8,
        wordBreak: "break-all",
      }}
    >
      提出済みファイル：{fileNames[it.key]}
    </div>

    {!submittedOrApproved && (
      <button
        type="button"
        onClick={async () => {
          const ok = window.confirm("アップロード済み書類を取消しますか？");
          if (!ok) return;

          try {
            setPageError("");

            const token = localStorage.getItem("merchant_access_token");

const res = await fetch(
  `/merchant/documents/${it.key}`,
  {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

const json = await res.json().catch(() => ({}));

if (!res.ok) {
  throw new Error(json?.detail || "書類の取消に失敗しました。");
}

            setUploaded((prev) => ({
              ...prev,
              [it.key]: false,
            }));

            setFileNames((prev) => ({
              ...prev,
              [it.key]: "",
            }));

            if (it.isOther) {
              setOtherTitles((prev) => ({
                ...prev,
                [it.key]: "",
              }));
            }

            try {
              const statusRes = await apiGet("/merchant/documents/status");

              const rows: UploadedDocItem[] = Array.isArray(statusRes?.items)
                ? statusRes.items
                : [];

              setUploadedDocs(rows);

              const missingLabels: string[] | null =
                Array.isArray(statusRes?.missing_required_labels)
                  ? statusRes.missing_required_labels
                  : Array.isArray(statusRes?.missing_required)
                  ? statusRes.missing_required
                      .map((x: any) => String(x?.label || ""))
                      .filter((s: string) => s.trim())
                  : null;

              setMissingRequiredLabels(missingLabels);
            } catch {
              // ignore refresh errors
            }
          } catch (e: any) {
            setPageError(e?.message || "書類の取消に失敗しました。");
          }
        }}
        style={{
          padding: "8px 14px",
          borderRadius: 10,
          border: "1px solid #fecaca",
          background: "#fff1f2",
          color: "#b91c1c",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        取消
      </button>
    )}
  </div>
)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 14, color: "#6b7280", fontSize: 12, lineHeight: 1.7 }}>
              ※ 提出済みの状態はバックエンドに保存され、再ログイン後も保持されます。
            </div>
          </div>
        


{/* Bottom Global Submit Button */}
<div
  style={{
    marginTop: 18,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  }}
>
  <div>
    <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 6 }}>全データ提出</div>
    <div style={{ color: "#475569", fontWeight: 900, fontSize: 13, lineHeight: 1.6 }}>
      フォーム入力と書類アップロード（必須）がすべて完了したら提出してください。
      <br />
      <span style={{ color: "#b91c1c" }}>提出後は編集できません。</span>
    </div>
  </div>

 <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
  <button
    type="button"
    onClick={() => navigate("/merchant/dashboard")}
    style={{
      padding: "12px 18px",
      borderRadius: 12,
      border: "1px solid #cbd5e1",
      background: "#ecfdf5",
      color: "#166534",
      fontWeight: 900,
      cursor: "pointer",
      minWidth: 100,
    }}
  >
    保存
  </button>

  <button
    onClick={onSubmitAll}
    disabled={submittedOrApproved || !canSubmit || submitting}
    style={submitBtnStyle}
  >
    {submitBtnLabel}
  </button>
</div>
</div>

        








                
        </>
      )}
    </MerchantLayout>
  );
}
